import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import type { SkillTreeResponse, CompetencyDimension } from './skill-tree-types';
import { SkillTree } from './SkillTree';

// Player "My Skills" page. Renders all 12 Forbury competency skill
// trees, grouped by dimension (4 technical, 8 behavioral). This is
// the M3 entry point — the per-competency visualisation the spec
// calls the "skill tree".
//
// One request to /api/me/skill-tree gives us everything; no N+1
// fetches per competency.
export function MySkillsPage() {
  const { data, isLoading, error } = useQuery<SkillTreeResponse>({
    queryKey: ['me-skill-tree'],
    queryFn: () => api<SkillTreeResponse>('/api/me/skill-tree'),
  });

  if (isLoading) return <div className="text-ink-muted">Loading skill tree…</div>;
  if (error) {
    const msg = error instanceof Error ? error.message : 'Failed to load skill tree.';
    return <div className="card text-accent-danger">{msg}</div>;
  }

  const competencies = data?.competencies ?? [];
  const technical = competencies.filter((c) => c.dimension === 'technical');
  const behavioral = competencies.filter((c) => c.dimension === 'behavioral');

  const totalXp = competencies.reduce((sum, c) => sum + c.xp, 0);
  // M5.8+ / ADR-0016: with the Initiate tier (tier 0) added,
  // each competency has 6 tiers (Initiate is always
  // unlocked by construction). The "Tiers unlocked" card
  // counts every lit node across all 12 competencies.
  const unlockedTotal = competencies.reduce(
    (sum, c) => sum + c.tiers.filter((t) => t.unlocked).length,
    0,
  );

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">My Skills</h1>
        <p className="text-ink-muted">
          Your progression across the 12 Forbury competencies. Each tree lights up
          as you earn XP from real engineering work.
        </p>
      </header>

      <div className="grid gap-4 md:grid-cols-3">
        <div className="card">
          <p className="label">Competencies</p>
          <p className="mt-1 text-4xl font-semibold tabular-nums">{competencies.length}</p>
        </div>
        <div className="card">
          <p className="label">Tiers unlocked</p>
          <p className="mt-1 text-4xl font-semibold tabular-nums">{unlockedTotal}<span className="text-lg text-ink-muted"> / {competencies.length * 6}</span></p>
        </div>
        <div className="card">
          <p className="label">Total per-competency XP</p>
          <p className="mt-1 text-4xl font-semibold tabular-nums">{totalXp}</p>
        </div>
      </div>

      <CompetencyGroup title="Technical" dimension="technical" competencies={technical} />
      <CompetencyGroup title="Behavioral" dimension="behavioral" competencies={behavioral} />
    </div>
  );
}

function CompetencyGroup({
  title,
  dimension,
  competencies,
}: {
  title: string;
  dimension: CompetencyDimension;
  competencies: SkillTreeResponse['competencies'];
}) {
  if (competencies.length === 0) return null;
  return (
    <section>
      <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-muted">
        {title} ({competencies.length})
      </h2>
      <div className="mt-3 grid gap-4 md:grid-cols-2">
        {competencies.map((c) => (
          <SkillTree key={c.id} competency={c} />
        ))}
      </div>
    </section>
  );
}